import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Post } from '../types';
import { postsService } from '../services/posts.service';
import { useAuth } from './AuthContext';

const POSTS_CHANGED_EVENT = 'intranet:postsChanged';

const POSTS_POLL_INTERVAL_MS = 60_000;

export const emitPostsChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(POSTS_CHANGED_EVENT));
};

export type RefreshPostsOptions = {
  silent?: boolean;
};

type PostsContextType = {
  posts: Post[];
  isLoading: boolean;
  error: string | null;
  refreshPosts: (options?: RefreshPostsOptions) => Promise<void>;
  lastUpdatedAt: number | null;
};

const PostsContext = createContext<PostsContextType | undefined>(undefined);

function scheduleAfterFirstPaint(fn: () => void, timeoutMs: number): () => void {
  const ric = window.requestIdleCallback;
  if (typeof ric === 'function') {
    const id = ric(fn, { timeout: timeoutMs });
    return () => window.cancelIdleCallback(id);
  }
  const t = window.setTimeout(fn, 0);
  return () => window.clearTimeout(t);
}

export const PostsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const inFlightRef = useRef(false);

  const refreshPosts = useCallback(async (options?: RefreshPostsOptions) => {
    if (!isAuthenticated) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const silent = options?.silent === true;
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const data = await postsService.getAll();
      setPosts(Array.isArray(data) ? data : []);
      setLastUpdatedAt(Date.now());
    } catch (e: any) {
      if (!silent) {
        setError(e?.response?.data?.message || e?.message || 'Failed to load posts');
      }
    } finally {
      if (!silent) setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPosts([]);
      setLastUpdatedAt(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const cancelSchedule = scheduleAfterFirstPaint(() => {
      void refreshPosts();
    }, 400);

    const interval = window.setInterval(
      () => refreshPosts({ silent: true }),
      POSTS_POLL_INTERVAL_MS,
    );

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshPosts({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelSchedule();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      inFlightRef.current = false;
    };
  }, [isAuthenticated, refreshPosts]);

  useEffect(() => {
    const handler = () => {
      refreshPosts({ silent: true });
    };
    window.addEventListener(POSTS_CHANGED_EVENT, handler as EventListener);
    return () => window.removeEventListener(POSTS_CHANGED_EVENT, handler as EventListener);
  }, [refreshPosts]);

  const value = useMemo<PostsContextType>(() => ({
    posts,
    isLoading,
    error,
    refreshPosts,
    lastUpdatedAt,
  }), [posts, isLoading, error, refreshPosts, lastUpdatedAt]);

  return <PostsContext.Provider value={value}>{children}</PostsContext.Provider>;
};

export const usePosts = (): PostsContextType => {
  const ctx = useContext(PostsContext);
  if (!ctx) throw new Error('usePosts must be used within a PostsProvider');
  return ctx;
};
